/**
 * The optics bench — docs/FeatureDesigns/optics/optics-bench-target.md.
 *
 * A student puts an object or a lamp on the rail, drops a lens, a mirror, a
 * prism or a glass block in front of it, and reads where the image is and
 * what it is like, with the formula worked in the syllabus's sign convention.
 *
 * All of the physics is in `@padcanvas/shared`'s optics engine — a separate
 * engine from the mechanics one, sharing no code with it — and none of it is
 * here. This file is layout, undo and the view; the scene is gestures and
 * drawing.
 *
 * The view (zoom, pan, grid, snap) is the student's: the bench fits itself
 * once, when it opens and when a preset loads, and never again on its own —
 * a view that rescaled while the object was dragged through F would make the
 * image look as if it stood still while the lens shrank.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CloseButton, ThemeToggle } from '../components/PaneButtons';
import '../components/BenchType.css';
import { useBenchFullscreen } from '../components/useBenchFullscreen';
import { useBenchTouch } from '../components/useBenchTouch';
import { FullscreenIcon } from '../components/FullscreenIcon';
import { BenchPhoneHeader } from '../components/BenchPhoneHeader';
import { optics } from '@padcanvas/shared';
import type { optics as O, labSetups as L } from '@padcanvas/shared';
import { useLabSetups, SaveSetupDialog, type LabSaving } from '../lab-setups';
import { BENCH_CHAPTER_OPTIONS } from '../simulations/benchSetups';
import { OpticsScene2D } from './OpticsScene2D';
import { PartIcon, type Glyph } from './PartIcons';
import { ResultsPane, Selection } from './ResultsPane';
import {
    MARK_SNAP_PX, OPENING_VIEW, SLIDER_MAX, ZOOM_MARKS, fitView, markFraction, nearestMark, niceStep, sliderToWidth,
    type View, widthForPercent,
    widthLabel, widthToSlider, zoomLabel, zoomPercent,
} from './view';
import './OpticsBench.css';

const { analyse, PRESETS, openingBench, presetByKey, place, setData, movePart, turnPart, removePart, setMedium, defOf } = optics;

type World = O.World;

interface Tile { glyph: Glyph; label: string; key: string; data?: Record<string, unknown> }

/** Whether a part on the bench is one this tile adds: the kind, and for the
 *  kinds with two tiles, which of the two — the mirror's curve, the lens's sign. */
function tileMatches(tile: Tile, part: O.Part, world: World): boolean {
    if (part.kind !== tile.key) return false;
    if (part.kind === 'sphericalMirror') return part.data.curve === tile.data?.curve;
    if (part.kind === 'lens') {
        const f = defOf(part).element?.(part, world).f ?? 1;
        return (tile.glyph === 'concaveLens') === (f < 0);
    }
    return true;
}

/** How much taller than true a phone draws heights, while every part is on the rail. */
const PHONE_STRETCH = 3;

/** The tray, in the parts catalogue's groups. Concave and convex are one kind each, two tiles. */
const TRAY: { title: string; tiles: Tile[] }[] = [
    {
        title: 'Light',
        tiles: [
            { glyph: 'object', label: 'Object', key: 'object' },
            { glyph: 'pointSource', label: 'Point source', key: 'pointSource' },
            { glyph: 'parallelBeam', label: 'Parallel beam', key: 'parallelBeam' },
            { glyph: 'rayBox', label: 'Ray box', key: 'rayBox' },
        ],
    },
    {
        title: 'Mirrors',
        tiles: [
            { glyph: 'planeMirror', label: 'Plane', key: 'planeMirror' },
            { glyph: 'concaveMirror', label: 'Concave', key: 'sphericalMirror', data: { curve: 'concave' } },
            { glyph: 'convexMirror', label: 'Convex', key: 'sphericalMirror', data: { curve: 'convex' } },
        ],
    },
    {
        title: 'Lenses',
        tiles: [
            { glyph: 'convexLens', label: 'Convex', key: 'lens' },
            { glyph: 'concaveLens', label: 'Concave', key: 'lens', data: { r1: -20, r2: 20, hold: 'radii' } },
        ],
    },
    {
        title: 'Blocks',
        tiles: [
            { glyph: 'prism', label: 'Prism', key: 'prism' },
            // Soft hyphens: a narrow tile breaks these at a syllable, not mid-letter.
            { glyph: 'slab', label: 'Rect\u00adangular', key: 'slab' },
            { glyph: 'semicircle', label: 'Semi\u00adcircular', key: 'semicircle' },
        ],
    },
    {
        title: 'Measure',
        tiles: [
            { glyph: 'screen', label: 'Screen', key: 'screen' },
            { glyph: 'stop', label: 'Stop', key: 'stop' },
        ],
    },
];

const MEDIA = [{ n: 1, label: 'Air' }, { n: 1.33, label: 'Water' }];

/** A phone, either way up — the chemistry lab's test. A phone held sideways
 *  is wider than any max-width rule for phones, so it is caught by its height. */
const PHONE = '(max-width: 640px), (orientation: landscape) and (max-height: 640px)';

/** Whether the phone layout is showing. The layout itself is CSS; this is for
 *  what the phone does differently — the parts pane's groups do not fold, and
 *  loading an arrangement turns to its results. */
function usePhone(): boolean {
    const query = (): MediaQueryList | null => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(PHONE) : null);
    const [phone, setPhone] = useState(() => query()?.matches ?? false);
    useEffect(() => {
        const mq = query();
        if (!mq) return;
        const on = (): void => setPhone(mq.matches);
        on();
        mq.addEventListener('change', on);
        return () => mq.removeEventListener('change', on);
    }, []);
    return phone;
}

/** On a phone, the one pane docked beside the bench. One is always showing:
 *  optics is laid out along a horizontal rail, so an upright phone has height
 *  to spare under the bench, and a pane there costs the drawing nothing. */
type Sheet = 'parts' | 'results' | 'presets';

interface History { world: World; past: World[]; future: World[] }
const HISTORY_CAP = 100;

export interface OpticsBenchProps {
    onBack?: () => void;
    /** Open on this world instead of the opening preset, fitted to the view —
     *  a question's scene. See docs/FeatureDesigns/question-scenes/question-scenes-target.md. */
    initialWorld?: World;
    /** Open on this textbook setup (a preset's key) rather than the opening one
     *  — how a list of setups sends a student to one. Ignored when
     *  `initialWorld` is given, or when no preset has the key. */
    initialPreset?: string;
    /** The name in the bench's header. Absent, the bench names itself. */
    title?: string;
    /** Saving to the teacher's own setups — docs/FeatureDesigns/lab-setups.
     *  Absent, the bench has no Save and no "My setups". Its `opened` setup is
     *  what the bench opens on, unless `initialWorld` is given. */
    saving?: LabSaving<L.OpticsSetupPayload>;
}

/** The version of `OpticsSetupPayload` this bench writes. */
const SETUP_PAYLOAD_VERSION = 1;
/** A saved setup's value in the setups picker, kept apart from the presets' keys. */
const SAVED = 'saved:';

export function OpticsBench({ onBack, initialWorld, initialPreset, title = 'Optics Bench', saving }: OpticsBenchProps): React.JSX.Element {
    const startWorld = initialWorld ?? saving?.opened?.payload.world;
    const [opening] = useState(() => (initialPreset && presetByKey(initialPreset)) || openingBench());
    const [history, setHistory] = useState<History>(() => ({ world: startWorld ?? opening.build(), past: [], future: [] }));
    /* Full screen, as the chemistry lab has it — the button only; see the
       mechanics bench for why the rotate-to-fill behaviour stays with chemistry. */
    const benchRef = useRef<HTMLDivElement | null>(null);
    const fullscreen = useBenchFullscreen(benchRef, { followRotation: false });
    useBenchTouch();
    const world = history.world;
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [presetKey, setPresetKey] = useState<string>(startWorld ? '' : opening.key);
    // The teacher's saved setups, when the host lets this bench save.
    const setups = useLabSetups<L.OpticsSetupPayload>('optics-bench', SETUP_PAYLOAD_VERSION, saving);
    const [saveDialog, setSaveDialog] = useState<'new' | 'copy' | null>(null);
    // 100% (2 m across) until the bench has measured itself; then it fits (below).
    const [view, setView] = useState<View>(OPENING_VIEW);
    const [size, setSize] = useState<{ w: number; h: number } | null>(null);
    const [showGrid, setShowGrid] = useState(true);
    const [snap, setSnap] = useState(true);
    const [showAngles, setShowAngles] = useState(true);
    const [showRail, setShowRail] = useState(true);
    const [showProps, setShowProps] = useState(true);
    // A phone opens on the results: the bench opens on a textbook arrangement,
    // and the results pane is what says what it shows.
    const phone = usePhone();
    const [sheet, setSheet] = useState<Sheet>('results');
    const railRef = useRef<HTMLDivElement | null>(null);
    // Dark unless the student chose light: rays read as light on a dark bench.
    // Only a toggle is remembered, under a key of its own — the old one was
    // written on every visit from the OS setting, so it says nothing about a choice.
    const [theme, setThemeState] = useState<'light' | 'dark'>(() => {
        try {
            if (window.localStorage.getItem('obench-theme-choice') === 'light') return 'light';
        } catch { /* storage blocked: dark */ }
        return 'dark';
    });
    const toggleTheme = useCallback(() => setThemeState(t => {
        const next = t === 'dark' ? 'light' : 'dark';
        try { window.localStorage.setItem('obench-theme-choice', next); } catch { /* not remembered, still applied */ }
        return next;
    }), []);

    const analysis = useMemo(() => analyse(world, { selectedId: selectedId ?? undefined }), [world, selectedId]);

    // ── undo ────────────────────────────────────────────────────────────────
    /** Every edit is one step of undo. */
    const edit = useCallback((f: (w: World) => World) => {
        setHistory(h => ({ world: f(h.world), past: [...h.past, h.world].slice(-HISTORY_CAP), future: [] }));
    }, []);
    /** A drag is one step: the world before it is pushed once, and the moves replace in place. */
    const beginDrag = useCallback(() => {
        setHistory(h => ({ ...h, past: [...h.past, h.world].slice(-HISTORY_CAP), future: [] }));
    }, []);
    const replace = useCallback((f: (w: World) => World) => setHistory(h => ({ ...h, world: f(h.world) })), []);
    const undo = useCallback(() => setHistory(h => (h.past.length
        ? { world: h.past[h.past.length - 1], past: h.past.slice(0, -1), future: [h.world, ...h.future] }
        : h)), []);
    const redo = useCallback(() => setHistory(h => (h.future.length
        ? { world: h.future[0], past: [...h.past, h.world], future: h.future.slice(1) }
        : h)), []);

    // ── the view ────────────────────────────────────────────────────────────
    // A scene is a deliberate "show me this", like loading a preset, so it fits.
    // Every bench opens fitted to what is on it — the opening arrangement is a
    // preset like any other, and landing on it at 100% left a student to press
    // Fit before the image could be read.
    const fitPending = useRef(true);
    /** A phone with everything on the rail draws heights ×3: the image's place,
     *  size and nature do not depend on heights there. Off the moment a part is
     *  lifted off or a block or prism comes on — angles are what those teach. */
    const stretchY = phone && world.parts.length > 0 && world.parts.every(p => p.mount === 'axis') ? PHONE_STRETCH : 1;
    const fitPoints = useCallback((extra: O.Vec2[] = []): O.Vec2[] => {
        const pts = world.parts.flatMap(p => defOf(p).outline(p, world));
        for (const r of analysis.images) {
            for (const p of r.points) {
                if ((p.kind === 'real' || p.kind === 'virtual') && Math.abs(p.at.x) < 2000 && Math.abs(p.at.y) < 1000) pts.push(p.at);
            }
        }
        return [...pts, ...extra];
    }, [world, analysis.images]);
    const fit = useCallback((extra: O.Vec2[] = []) => {
        if (!size) return;
        setView(fitView(fitPoints(extra), size.h / size.w / stretchY));
    }, [size, fitPoints, stretchY]);
    useEffect(() => {
        if (size && fitPending.current) {
            fitPending.current = false;
            fit();
        }
    }, [size, fit]);

    // ── editing ─────────────────────────────────────────────────────────────
    const addPart = (tile: Tile): void => {
        // Placed against the world as it is now, so the new id is known here —
        // an updater passed to setState runs later, too late to select it.
        const placed = place(world, tile.key);
        const next = tile.data ? setData(placed.world, placed.id, tile.data) : placed.world;
        edit(() => next);
        setSelectedId(placed.id);
        setPresetKey('');
    };

    const remove = useCallback((id: string) => {
        edit(w => removePart(w, id));
        setSelectedId(null);
    }, [edit]);

    /** Every part goes; the bench's settings (medium, angles) stay. */
    const clearBench = (): void => {
        edit(w => ({ ...w, parts: [] }));
        setSelectedId(null);
        setPresetKey('');
    };

    const loadPreset = (key: string): void => {
        setPresetKey(key);
        const p = presetByKey(key);
        if (!p) return;
        edit(() => p.build());
        setSelectedId(null);
        setups.leave();
        fitPending.current = true;
        // Straight to what the arrangement shows, with the textbook's claim.
        if (phone) setSheet('results');
    };

    /** One of the teacher's own setups, as one step of undo, like a preset. */
    const loadSaved = async (id: string): Promise<void> => {
        const payload = await setups.load(id);
        if (!payload) return;
        setPresetKey('');
        edit(() => payload.world);
        setSelectedId(null);
        fitPending.current = true;
        if (phone) setSheet('results');
    };
    const pick = (value: string): void => {
        if (value.startsWith(SAVED)) void loadSaved(value.slice(SAVED.length));
        else if (value) loadPreset(value);
    };

    // ── saving ──────────────────────────────────────────────────────────────
    /** Whether the bench differs from the saved setup it is showing. Every edit
     *  makes a new world, and undoing back to the saved one is clean again. */
    const unsaved = setups.enabled && setups.current ? setups.current.saved.world !== world : false;
    /** Save over the open setup; with none open, name a new one first. */
    const saveNow = (): void => {
        if (setups.busy) return;
        if (setups.current) void setups.save({ payload: { world } });
        else setSaveDialog('new');
    };
    const saveFromDialog = async (name: string, notes: string, chapterIds?: string[]): Promise<void> => {
        const ok = await setups.save({
            payload: { world }, name, notes, asNew: true,
            basedOn: presetKey ? { preset: presetKey } : undefined,
            chapterIds,
        });
        if (ok) setSaveDialog(null);
    };
    const shownTitle = setups.current ? setups.current.name : title;
    // Refit after the preset's world is in (the effect above runs on size, so nudge it).
    useEffect(() => {
        if (fitPending.current && size) {
            fitPending.current = false;
            fit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [world]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            const target = e.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA')) return;
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's' && setups.enabled) {
                // The browser's own Save Page is never what a teacher on the bench means.
                e.preventDefault();
                saveNow();
            } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                if (e.shiftKey) redo(); else undo();
            } else if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
                e.preventDefault();
                remove(selectedId);
            } else if (e.key === 'Escape') {
                setSelectedId(null);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [undo, redo, remove, selectedId, setups.enabled, setups.current, setups.busy, world]);

    // ── the zoom slider's marks ──────────────────────────────────────────────
    // A click near the scale goes to the nearest mark; a drag snaps onto a mark
    // it comes within MARK_SNAP_PX of. Keyboard steps are left alone — each is
    // under a pixel, and snapping them would hold the thumb on the mark for ever.
    const zoomInput = useRef<HTMLInputElement>(null);
    const zoomPress = useRef<{ x: number; moved: boolean } | null>(null);
    /** 0–1 along the track for a pointer x, allowing for the thumb's inset. */
    const trackFraction = (clientX: number): number => {
        const r = (zoomInput.current as HTMLInputElement).getBoundingClientRect();
        return Math.min(1, Math.max(0, (clientX - r.left - 8) / Math.max(1, r.width - 16)));
    };
    const onZoomSlide = (value: number): void => {
        let width = sliderToWidth(value);
        const input = zoomInput.current;
        if (zoomPress.current && input) {
            const f = value / SLIDER_MAX;
            const mark = nearestMark(f);
            if (Math.abs(markFraction(mark) - f) * (input.getBoundingClientRect().width - 16) <= MARK_SNAP_PX) {
                width = widthForPercent(mark);
            }
        }
        setView(v => ({ ...v, width }));
    };
    const zoomPointer = {
        onPointerDown: (e: React.PointerEvent) => { zoomPress.current = { x: e.clientX, moved: false }; },
        onPointerMove: (e: React.PointerEvent) => {
            const p = zoomPress.current;
            if (p && Math.abs(e.clientX - p.x) > 4) p.moved = true;
        },
        onPointerUp: (e: React.PointerEvent) => {
            const p = zoomPress.current;
            zoomPress.current = null;
            if (p && !p.moved) {
                const mark = nearestMark(trackFraction(e.clientX));
                setView(v => ({ ...v, width: widthForPercent(mark) }));
            }
        },
        onPointerCancel: () => { zoomPress.current = null; },
    };

    // A phone: a part picked — on the bench, from a chip, or just added — brings
    // its tile and the settings under it into view in the parts pane, rather than
    // leaving them wherever the pane was last scrolled.
    useEffect(() => {
        if (!phone || sheet !== 'parts' || !selectedId) return;
        const frame = requestAnimationFrame(() => {
            const rail = railRef.current;
            const tile = rail?.querySelector<HTMLElement>('.obench-tile[data-active]');
            if (!rail || !tile) return;
            const top = tile.getBoundingClientRect().top - rail.getBoundingClientRect().top + rail.scrollTop - 8;
            rail.scrollTo({ top, behavior: 'smooth' });
        });
        return () => cancelAnimationFrame(frame);
    }, [phone, sheet, selectedId]);

    const preset = presetKey ? PRESETS.find(p => p.key === presetKey) : undefined;
    const selectedPart = selectedId ? world.parts.find(p => p.id === selectedId) : undefined;
    const pxPerCm = size ? size.w / view.width : 1;
    // A round length whose bar is 60–150 px, so it always fits its fixed slot and is
    // always drawn exactly as long as it says.
    const barCm = niceStep(60 / pxPerCm);

    return (
        <div className="obench bench-palette" ref={benchRef} data-theme={theme} data-rail={showRail ? 'open' : 'hidden'} data-props={showProps ? 'open' : 'hidden'} data-sheet={sheet} data-full={fullscreen.pinned ? 'true' : undefined}>
            <div className="obench-rail" ref={railRef}>
                <CloseButton className="obench-paneclose" onClick={() => setShowRail(false)} label="Hide the parts pane" />
                {onBack && <button type="button" className="obench-back obench-wide-only" onClick={onBack}>← Back</button>}

                {/* Beside the controls that fix them, never laid over the drawing. */}
                {analysis.problems.length > 0 && (
                    <ul className="obench-alert">
                        {analysis.problems.map((p, i) => (
                            <li key={i} data-level={p.level}>
                                <b aria-hidden="true">{p.level === 'stops' ? '!' : 'i'}</b>
                                <span>{p.text}</span>
                            </li>
                        ))}
                    </ul>
                )}

                {TRAY.map(group => {
                    // A phone: the selected part's tile is lit, and its settings open
                    // under this group — beside what made it, not in the results.
                    const here = phone && selectedPart ? group.tiles.find(t => tileMatches(t, selectedPart, world)) : undefined;
                    const same = here ? world.parts.filter(p => tileMatches(here, p, world)) : [];
                    return (
                        <RailGroup fixed={phone} key={group.title} title={group.title}>
                            <div className="obench-tiles">
                                {group.tiles.map(t => (
                                    <button key={t.label + t.key} type="button" className="obench-tile" data-active={t === here ? 'true' : undefined}
                                        title={`Add a ${t.label.replace(/\u00ad/g, '').toLowerCase()} ${group.title === 'Light' ? '' : group.title.toLowerCase().replace(/s$/, '')}`.trim()} onClick={() => addPart(t)}>
                                        <PartIcon glyph={t.glyph} />
                                        <span>{t.label}</span>
                                    </button>
                                ))}
                            </div>
                            {here && selectedPart && (
                                <div className="obench-partprops">
                                    {/* Two of one kind: say which is open, and switch in one tap. */}
                                    {same.length > 1 && (
                                        <div className="obench-partpick" role="group" aria-label={`Which ${here.label.replace(/\u00ad/g, '').toLowerCase()}`}>
                                            {same.map(p => (
                                                <button key={p.id} type="button" aria-pressed={p.id === selectedPart.id} onClick={() => setSelectedId(p.id)}>
                                                    {defOf(p).name(p, world)}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <Selection part={selectedPart} world={world} onEdit={edit} onRemove={remove} theme={theme} />
                                </div>
                            )}
                        </RailGroup>
                    );
                })}

                <RailGroup fixed={phone} title="The bench" open={false}>
                    <div className="obench-choice">
                        <span>Surrounded by</span>
                        <div className="obench-seg">
                            {MEDIA.map(m => (
                                <button key={m.label} type="button" aria-pressed={world.medium === m.n}
                                    onClick={() => edit(w => setMedium(w, m.n))}>{m.label} {m.n.toFixed(2)}</button>
                            ))}
                        </div>
                    </div>
                    <label className="obench-check">
                        <input type="checkbox" checked={showAngles} onChange={e => setShowAngles(e.target.checked)} />
                        Normals and angles at each face
                    </label>
                </RailGroup>

                <RailGroup fixed={phone} title={setups.enabled ? 'Setups' : 'Try one'} className="obench-wide-only">
                    <select value={setups.current ? SAVED + setups.current.id : presetKey} onChange={e => pick(e.target.value)}
                        aria-label={setups.enabled ? 'Open a saved setup or a textbook arrangement' : 'Load a textbook arrangement'}>
                        <option value="">— your own build —</option>
                        {setups.enabled ? (
                            <>
                                {/* The open one is listed even before the list arrives, so the select can show it. */}
                                <optgroup label="My setups">
                                    {setups.current && !setups.mine.some(m => m.id === setups.current?.id) && (
                                        <option value={SAVED + setups.current.id}>{setups.current.name}</option>
                                    )}
                                    {setups.mine.map(m => <option key={m.id} value={SAVED + m.id}>{m.name}</option>)}
                                    {setups.mine.length === 0 && !setups.current && <option disabled value="-">Nothing saved yet</option>}
                                </optgroup>
                                <optgroup label="Textbook">
                                    {PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                                </optgroup>
                            </>
                        ) : PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                </RailGroup>
            </div>

            <div className="obench-main">
                <BenchPhoneHeader
                    title={shownTitle + (unsaved ? ' •' : '')}
                    back={onBack ? { label: 'Back', onClick: onBack } : undefined}
                    theme={theme}
                    onToggleTheme={toggleTheme}
                    fullscreen={fullscreen}
                />
                <div className="obench-top">
                    <div className="obench-top-left">
                        {/* A phone's way out sits here: the parts pane that holds it on
                            a wide screen is closed. */}
                        {onBack && (
                            <button type="button" className="obench-iconbtn obench-phone-only" onClick={onBack} title="Back" aria-label="Back">←</button>
                        )}
                        <button type="button" className="obench-panetoggle obench-wide-only" aria-pressed={showRail} onClick={() => setShowRail(v => !v)}
                            title={showRail ? 'Hide the parts pane' : 'Show the parts pane'} aria-label={showRail ? 'Hide the parts pane' : 'Show the parts pane'}>◧</button>
                        <button type="button" className="obench-iconbtn" onClick={undo} disabled={history.past.length === 0} title="Undo" aria-label="Undo">↶</button>
                        <button type="button" className="obench-iconbtn" onClick={redo} disabled={history.future.length === 0} title="Redo" aria-label="Redo">↷</button>
                        {/* No "are you sure?": clearing is one step of undo, so a mistaken
                            click costs one ⌘Z rather than a dialog on every deliberate one. */}
                        <button type="button" className="obench-textbtn obench-wide-only" onClick={clearBench} disabled={world.parts.length === 0}
                            title="Remove every part (undo brings them back)">Clear bench</button>
                        {setups.enabled && (
                            <>
                                <button type="button" className="obench-textbtn obench-savebtn obench-wide-only" onClick={saveNow}
                                    disabled={setups.busy || (!!setups.current && !unsaved)}
                                    title={setups.current ? `Save the changes to “${setups.current.name}” (⌘S)` : 'Save this arrangement to My setups (⌘S)'}>
                                    {setups.busy ? 'Saving…' : setups.current && !unsaved ? 'Saved' : 'Save'}
                                </button>
                                {setups.current && (
                                    <button type="button" className="obench-textbtn obench-savebtn obench-wide-only" onClick={() => setSaveDialog('copy')}
                                        disabled={setups.busy} title="Save this arrangement as a new setup, leaving the open one as it was">Save as…</button>
                                )}
                                {setups.error && !saveDialog && <span className="obench-saveerr" role="alert">Not saved: {setups.error}</span>}
                            </>
                        )}
                    </div>
                    <h2>
                        {shownTitle}
                        {unsaved && <span className="obench-unsaved" title="Changed since it was saved"> •</span>}
                    </h2>
                    <div className="obench-top-right obench-wide-only">
                        <ThemeToggle theme={theme} onToggle={toggleTheme} />
                        <button
                            type="button"
                            className="obench-panetoggle obench-fullscreen"
                            aria-pressed={fullscreen.isFullscreen}
                            onClick={fullscreen.toggle}
                            title={fullscreen.isFullscreen ? 'Leave full screen' : 'Fill the screen with the bench'}
                            aria-label={fullscreen.isFullscreen ? 'Leave full screen' : 'Full screen'}
                        >
                            <FullscreenIcon exiting={fullscreen.isFullscreen} />
                        </button>
                        <button type="button" className="obench-panetoggle" aria-pressed={showProps} onClick={() => setShowProps(v => !v)}
                            title={showProps ? 'Hide the results pane' : 'Show the results pane'} aria-label={showProps ? 'Hide the results pane' : 'Show the results pane'}>◨</button>
                    </div>
                </div>

                <div className="obench-stage bench-accent-wash">
                    <OpticsScene2D
                        world={world}
                        analysis={analysis}
                        view={view}
                        onView={setView}
                        onSize={setSize}
                        selectedId={selectedId}
                        // A phone turns to the parts pane, where a part's settings sit under its tile.
                        onSelect={id => { setSelectedId(id); if (phone && id) setSheet('parts'); }}
                        onDragStart={beginDrag}
                        onMove={(id, at) => replace(w => movePart(w, id, at))}
                        onTurn={(id, angle) => replace(w => turnPart(w, id, angle))}
                        onReveal={p => fit([p])}
                        showGrid={showGrid}
                        snap={snap}
                        showAngles={showAngles && stretchY === 1}
                        stretchY={stretchY}
                        theme={theme}
                    />
                    {stretchY !== 1 && (
                        <span className="obench-stretchtag" title="Heights are drawn taller than true so the object and image can be seen. Positions and the formula are unaffected.">
                            Heights ×{stretchY}
                        </span>
                    )}
                    {/* A phone: the top bar has no room for these, so they sit on the
                        bench, in the corner the ray animation leaves free. */}
                    <div className="obench-stagetools obench-phone-only">
                        <button type="button" className="obench-stagebtn" onClick={clearBench} disabled={world.parts.length === 0}
                            title="Clear bench (undo brings the parts back)" aria-label="Clear bench">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M6 6l1 14h10l1-14" />
                            </svg>
                        </button>
                        {setups.enabled && (
                            <button type="button" className="obench-stagebtn" onClick={saveNow}
                                disabled={setups.busy || (!!setups.current && !unsaved)}
                                title={setups.current ? 'Save the changes' : 'Save to My setups'} aria-label="Save">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M5 3h11l3 3v15H5z" /><path d="M8 3v5h7V3" /><path d="M8 21v-7h8v7" />
                                </svg>
                            </button>
                        )}
                        <ThemeToggle theme={theme} onToggle={toggleTheme} />
                        <button type="button" className="obench-stagebtn obench-stage-fullscreen" aria-pressed={fullscreen.isFullscreen} onClick={fullscreen.toggle}
                            title={fullscreen.isFullscreen ? 'Leave full screen' : 'Full screen'}
                            aria-label={fullscreen.isFullscreen ? 'Leave full screen' : 'Full screen'}>
                            <FullscreenIcon exiting={fullscreen.isFullscreen} />
                        </button>
                    </div>
                    {/* Grid and Snap are about the drawing and the drag, so they sit on the
                        bench, in the corner opposite the scale. */}
                    <div className="obench-benchtools">
                        <button type="button" aria-pressed={showGrid} onClick={() => setShowGrid(g => !g)}>Grid</button>
                        <button type="button" aria-pressed={snap} onClick={() => setSnap(s => !s)} title="Hold Alt while dragging to move freely">Snap</button>
                    </div>
                    {/* The scale, on the drawing it measures, as on a map. */}
                    <span className="obench-scalebar" aria-label={`Scale: this bar is ${widthLabel(barCm)}`}>
                        <i style={{ width: `${barCm * pxPerCm}px` }} />
                        <span>{widthLabel(barCm)}</span>
                    </span>
                </div>

                {/* The view's own controls, at the bench's foot. */}
                <div className="obench-viewbar">
                    <div className="obench-zoom">
                        <span>Zoom</span>
                        <div className="obench-zoomtrack" {...zoomPointer}>
                            <input
                                ref={zoomInput}
                                type="range" min={0} max={SLIDER_MAX} step={1} aria-label="Zoom"
                                value={Math.round(widthToSlider(view.width))}
                                aria-valuetext={`${zoomLabel(view.width)}, ${widthLabel(view.width)} across`}
                                onChange={e => onZoomSlide(+e.target.value)}
                            />
                            {/* The current zoom rides on the thumb, where the eye already is. */}
                            <span className="obench-zoombubble" aria-hidden="true"
                                style={{ '--f': widthToSlider(view.width) / SLIDER_MAX } as React.CSSProperties}>
                                {zoomLabel(view.width)}
                            </span>
                            {/* The levels a student names, marked where they fall on the track.
                                Clicking one goes there, so 100% is also the way back. */}
                            <div className="obench-zoommarks">
                                {ZOOM_MARKS.map(pct => (
                                    <button
                                        key={pct} type="button" data-pct={pct}
                                        style={{ '--f': markFraction(pct) } as React.CSSProperties}
                                        aria-pressed={Math.abs(zoomPercent(view.width) - pct) < 0.5}
                                        title={`${pct}%: ${widthLabel(widthForPercent(pct))} across`}
                                    >{pct}%</button>
                                ))}
                            </div>
                        </div>
                        {/* The distance stays put at the end of the scale; only the % moves. */}
                        <em>{widthLabel(view.width)} across</em>
                    </div>
                    <button type="button" onClick={() => fit()} title="Show every part and image, in the middle 80% of the view">Fit</button>
                </div>
            </div>

            <aside className="obench-props">
                <CloseButton className="obench-paneclose" onClick={() => setShowProps(false)} label="Hide the results pane" />
                <ResultsPane world={world} analysis={analysis} selectedId={selectedId} onEdit={edit} onRemove={remove} theme={theme}
                    claim={preset ? { label: preset.label, text: preset.claim }
                        : setups.current?.notes ? { label: setups.current.name, text: setups.current.notes } : undefined}
                    showSelection={!phone} />
            </aside>

            {/* A phone's arrangements: a list to tap, not a select to scroll —
                each names what it shows, and the bench fits to it. */}
            <div className="obench-presets">
                {setups.enabled && setups.mine.length > 0 && (
                    <>
                        <h3 className="obench-panehead">My setups</h3>
                        <ul>
                            {setups.mine.map(m => (
                                <li key={m.id}>
                                    <button type="button" aria-pressed={setups.current?.id === m.id} onClick={() => void loadSaved(m.id)}>{m.name}</button>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                <h3 className="obench-panehead">Try one</h3>
                <ul>
                    {PRESETS.map(p => (
                        <li key={p.key}>
                            <button type="button" aria-pressed={presetKey === p.key} onClick={() => loadPreset(p.key)}>{p.label}</button>
                        </li>
                    ))}
                </ul>
            </div>

            {/* A phone's panes, one showing at a time, docked under the bench (or
                beside it, sideways). Hidden wider, where every pane has a column. */}
            <nav className="obench-sheetbar obench-phone-only" aria-label="Panes">
                <button type="button" aria-pressed={sheet === 'parts'} onClick={() => setSheet('parts')}>
                    Parts
                    {/* The problems live at the top of the parts pane; say they are there. */}
                    {analysis.problems.length > 0 && (
                        <b className="obench-sheetbadge" aria-label={`${analysis.problems.length} to fix`}>{analysis.problems.length}</b>
                    )}
                </button>
                <button type="button" aria-pressed={sheet === 'results'} onClick={() => setSheet('results')}>Results</button>
                <button type="button" aria-pressed={sheet === 'presets'} onClick={() => setSheet('presets')}>{setups.enabled ? 'Setups' : 'Try one'}</button>
            </nav>

            {saveDialog && (
                <SaveSetupDialog
                    heading={saveDialog === 'copy' ? 'Save as a new setup' : 'Save to My setups'}
                    initialName={saveDialog === 'copy' && setups.current ? `${setups.current.name} (copy)` : preset?.label ?? 'My optics setup'}
                    initialNotes={saveDialog === 'copy' ? setups.current?.notes : undefined}
                    chapters={BENCH_CHAPTER_OPTIONS.optics}
                    initialChapterIds={saveDialog === 'copy' ? setups.current?.chapterIds : undefined}
                    busy={setups.busy}
                    error={setups.error}
                    onCancel={() => setSaveDialog(null)}
                    onSave={(name, notes, chapterIds) => void saveFromDialog(name, notes, chapterIds)}
                />
            )}
        </div>
    );
}

function RailGroup({ title, children, open: start = true, className = '', fixed = false }: {
    title: string;
    children: React.ReactNode;
    open?: boolean;
    className?: string;
    /** Always open, its head a plain heading — a phone's sheet, where the
     *  pane scrolls and a fold is only one more thing to tap. */
    fixed?: boolean;
}): React.JSX.Element {
    const [open, setOpen] = useState(start);
    if (fixed) {
        return (
            <section className={`obench-group ${className}`.trim()} data-open="true">
                <h3 className="obench-grouphead">{title}</h3>
                <div className="obench-groupbody">{children}</div>
            </section>
        );
    }
    return (
        <section className={`obench-group ${className}`.trim()} data-open={open}>
            <button type="button" className="obench-grouphead" onClick={() => setOpen(o => !o)} aria-expanded={open}>
                {title}<span aria-hidden="true">⌃</span>
            </button>
            {open && <div className="obench-groupbody">{children}</div>}
        </section>
    );
}
